import { useState, useEffect } from 'react'
import { Box, Heading, Text, SimpleGrid, Flex, Button, Table, Badge, IconButton } from '@chakra-ui/react'
import { Plus, Calendar, Key, BookOpen, Play, Lock, Edit, Archive, Trash2 } from 'lucide-react'
import { useFiscalYears, useStartFiscalYear, useCloseFiscalYear, useDistributionKeys, useResidenceChartAccounts, useInitializeResidenceChartAccounts, useArchiveDistributionKey, useDeleteDistributionKey } from '../../hooks'
import { ChartAccountsManageModal, DistributionKeyModal } from '../../components'
import { ConfirmDeleteDialog } from '@/shared/components/ConfirmDeleteDialog'
import { accountingToaster as toaster } from '../AccountingLayout'
import { useAccountingContext } from '../../context/AccountingContext'
import { supabase } from '@/lib/supabase'
import type { DistributionKey } from '../../types'

export function SettingsTab() {
  const { residenceId, setIsFiscalYearModalOpen } = useAccountingContext()
  const onCreateFiscalYear = () => setIsFiscalYearModalOpen(true)
  const { data: fiscalYears, isLoading: fyLoading } = useFiscalYears(residenceId)
  const { data: distributionKeys, isLoading: dkLoading } = useDistributionKeys(residenceId)
  const { data: chartAccounts, isLoading: caLoading } = useResidenceChartAccounts(residenceId)
  const initializeChartAccounts = useInitializeResidenceChartAccounts()
  const startFiscalYear = useStartFiscalYear()
  const closeFiscalYear = useCloseFiscalYear()
  const archiveDistributionKey = useArchiveDistributionKey()
  const deleteDistributionKey = useDeleteDistributionKey()

  // États pour les modales
  const [isChartAccountsModalOpen, setIsChartAccountsModalOpen] = useState(false)
  const [isDistributionKeyModalOpen, setIsDistributionKeyModalOpen] = useState(false)
  const [editingDistributionKeyId, setEditingDistributionKeyId] = useState<string | undefined>()
  const [deleteDialogKey, setDeleteDialogKey] = useState<DistributionKey | null>(null)
  const [keysUsage, setKeysUsage] = useState<Record<string, boolean>>({})

  // Charger l'état d'utilisation des clés
  useEffect(() => {
    if (!distributionKeys?.length) return

    const checkAllUsages = async () => {
      const usageMap: Record<string, boolean> = {}
      for (const dk of distributionKeys) {
        const { count } = await supabase
          .from('budget_lines')
          .select('*', { count: 'exact', head: true })
          .eq('distribution_key_id', dk.id)
        usageMap[dk.id] = (count ?? 0) > 0
      }
      setKeysUsage(usageMap)
    }
    checkAllUsages()
  }, [distributionKeys])

  const handleInitializeChartAccounts = async () => {
    await initializeChartAccounts.mutateAsync(residenceId)
  }

  const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object') {
      const err = error as { message?: string; details?: string; hint?: string; code?: string }
      // PostgreSQL error messages are in 'message' field
      if (err.message) return err.message
      if (err.details) return err.details
      if (err.hint) return err.hint
    }
    if (error instanceof Error) {
      return error.message
    }
    return 'Une erreur est survenue'
  }

  const handleStartFiscalYear = async (id: string) => {
    try {
      await startFiscalYear.mutateAsync(id)
      toaster.create({ title: 'Succès', description: 'Exercice démarré avec succès', type: 'success' })
    } catch (error: unknown) {
      toaster.create({ title: 'Erreur', description: getErrorMessage(error), type: 'error' })
    }
  }

  const handleCloseFiscalYear = async (id: string) => {
    try {
      await closeFiscalYear.mutateAsync(id)
      toaster.create({ title: 'Succès', description: 'Exercice clôturé avec succès', type: 'success' })
    } catch (error: unknown) {
      toaster.create({ title: 'Erreur', description: getErrorMessage(error), type: 'error' })
    }
  }

  const getStatusBadge = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      draft: { color: 'blue', label: 'Créé' },
      open: { color: 'green', label: 'En cours' },
      closed: { color: 'gray', label: 'Clôturé' },
    }
    const { color, label } = config[status] || { color: 'gray', label: status }
    return (
      <Badge colorPalette={color} variant="subtle">
        {label}
      </Badge>
    )
  }

  const handleArchiveKey = async (dk: DistributionKey) => {
    try {
      await archiveDistributionKey.mutateAsync(dk.id)
      toaster.create({ title: 'Succès', description: 'Clé archivée avec succès', type: 'success' })
    } catch (error: unknown) {
      toaster.create({ title: 'Erreur', description: getErrorMessage(error), type: 'error' })
    }
  }

  const handleConfirmDeleteKey = async () => {
    if (!deleteDialogKey) return
    await deleteDistributionKey.mutateAsync(deleteDialogKey.id)
    toaster.create({ title: 'Succès', description: 'Clé supprimée avec succès', type: 'success' })
  }

  return (
    <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
      {/* Exercices comptables */}
      <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
        <Flex justify="space-between" align="center" mb={4}>
          <Flex align="center" gap={2}>
            <Calendar size={20} />
            <Heading size="md">Exercices comptables</Heading>
          </Flex>
          <Button
            size="sm"
            colorPalette="brand"
            onClick={onCreateFiscalYear}
          >
            <Plus size={16} />
            Nouveau
          </Button>
        </Flex>

        {fyLoading ? (
          <Text>Chargement...</Text>
        ) : fiscalYears && fiscalYears.length > 0 ? (
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Nom</Table.ColumnHeader>
                <Table.ColumnHeader>Période</Table.ColumnHeader>
                <Table.ColumnHeader>Statut</Table.ColumnHeader>
                <Table.ColumnHeader>Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {fiscalYears.map((fy) => (
                <Table.Row key={fy.id}>
                  <Table.Cell fontWeight="medium">{fy.name}</Table.Cell>
                  <Table.Cell fontSize="sm">
                    {new Date(fy.start_date).toLocaleDateString('fr-FR')} -{' '}
                    {new Date(fy.end_date).toLocaleDateString('fr-FR')}
                  </Table.Cell>
                  <Table.Cell>{getStatusBadge(fy.status)}</Table.Cell>
                  <Table.Cell>
                    <Flex gap={1}>
                      {fy.status === 'draft' && (
                        <IconButton
                          aria-label="Démarrer l'exercice"
                          size="xs"
                          colorPalette="green"
                          variant="ghost"
                          onClick={() => handleStartFiscalYear(fy.id)}
                          title="Démarrer l'exercice"
                        >
                          <Play size={14} />
                        </IconButton>
                      )}
                      {fy.status === 'open' && (
                        <IconButton
                          aria-label="Clôturer l'exercice"
                          size="xs"
                          colorPalette="orange"
                          variant="ghost"
                          onClick={() => handleCloseFiscalYear(fy.id)}
                          title="Clôturer l'exercice"
                        >
                          <Lock size={14} />
                        </IconButton>
                      )}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        ) : (
          <Box textAlign="center" py={6} color="gray.500">
            <Text>Aucun exercice comptable</Text>
          </Box>
        )}
      </Box>

      {/* Clés de répartition */}
      <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
        <Flex justify="space-between" align="center" mb={4}>
          <Flex align="center" gap={2}>
            <Key size={20} />
            <Heading size="md">Clés de répartition</Heading>
          </Flex>
          <Button
            size="sm"
            colorPalette="brand"
            onClick={() => {
              setEditingDistributionKeyId(undefined)
              setIsDistributionKeyModalOpen(true)
            }}
          >
            <Plus size={16} />
            Nouvelle clé
          </Button>
        </Flex>

        {dkLoading ? (
          <Text>Chargement...</Text>
        ) : distributionKeys && distributionKeys.length > 0 ? (
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Nom</Table.ColumnHeader>
                <Table.ColumnHeader>Type</Table.ColumnHeader>
                <Table.ColumnHeader>Base</Table.ColumnHeader>
                <Table.ColumnHeader></Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {distributionKeys.filter((dk) => dk.is_active !== false).map((dk) => (
                <Table.Row key={dk.id}>
                  <Table.Cell fontWeight="medium">{dk.name}</Table.Cell>
                  <Table.Cell>
                    <Badge variant="outline">
                      {dk.key_type === 'general' ? 'Générale' : 'Spéciale'}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell fontSize="sm" color="gray.600">
                    {dk.calculation_base}
                  </Table.Cell>
                  <Table.Cell>
                    <Flex gap={1}>
                      <IconButton
                        aria-label="Modifier"
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          setEditingDistributionKeyId(dk.id)
                          setIsDistributionKeyModalOpen(true)
                        }}
                      >
                        <Edit size={14} />
                      </IconButton>
                      {keysUsage[dk.id] ? (
                        <IconButton
                          aria-label="Archiver"
                          size="xs"
                          variant="ghost"
                          colorPalette="orange"
                          onClick={() => handleArchiveKey(dk)}
                          title="Archiver la clé"
                        >
                          <Archive size={14} />
                        </IconButton>
                      ) : (
                        <IconButton
                          aria-label="Supprimer"
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => setDeleteDialogKey(dk)}
                          title="Supprimer la clé"
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      )}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        ) : (
          <Box textAlign="center" py={6} color="gray.500">
            <Text>Aucune clé de répartition</Text>
          </Box>
        )}
      </Box>

      {/* Plan comptable */}
      <Box bg="white" p={5} borderRadius="lg" boxShadow="sm" gridColumn={{ lg: 'span 2' }}>
        <Flex justify="space-between" align="center" mb={4}>
          <Flex align="center" gap={2}>
            <BookOpen size={20} />
            <Heading size="md">Plan comptable</Heading>
          </Flex>
          <Flex gap={2}>
            {(!chartAccounts || chartAccounts.length === 0) && (
              <Button
                size="sm"
                colorPalette="green"
                onClick={handleInitializeChartAccounts}
                loading={initializeChartAccounts.isPending}
              >
                Initialiser le plan comptable
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsChartAccountsModalOpen(true)}
              disabled={!chartAccounts || chartAccounts.length === 0}
            >
              Gérer le plan comptable
            </Button>
          </Flex>
        </Flex>

        {caLoading ? (
          <Text>Chargement...</Text>
        ) : chartAccounts && chartAccounts.length > 0 ? (
          <Box>
            <Text color="gray.600" mb={2}>
              {chartAccounts.length} comptes configurés pour cette résidence
            </Text>
            <SimpleGrid columns={{ base: 2, md: 5 }} gap={4}>
              <Box p={3} bg="orange.50" borderRadius="md">
                <Text fontSize="sm" color="orange.600">
                  Classe 1 - Capitaux
                </Text>
                <Text fontWeight="bold" color="orange.700">
                  {chartAccounts.filter((c) => c.chart_account?.code?.startsWith('1')).length}
                </Text>
              </Box>
              <Box p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" color="blue.600">
                  Classe 4 - Tiers
                </Text>
                <Text fontWeight="bold" color="blue.700">
                  {chartAccounts.filter((c) => c.chart_account?.code?.startsWith('4')).length}
                </Text>
              </Box>
              <Box p={3} bg="green.50" borderRadius="md">
                <Text fontSize="sm" color="green.600">
                  Classe 5 - Banques
                </Text>
                <Text fontWeight="bold" color="green.700">
                  {chartAccounts.filter((c) => c.chart_account?.code?.startsWith('5')).length}
                </Text>
              </Box>
              <Box p={3} bg="red.50" borderRadius="md">
                <Text fontSize="sm" color="red.600">
                  Classe 6 - Charges
                </Text>
                <Text fontWeight="bold" color="red.700">
                  {chartAccounts.filter((c) => c.chart_account?.code?.startsWith('6')).length}
                </Text>
              </Box>
              <Box p={3} bg="purple.50" borderRadius="md">
                <Text fontSize="sm" color="purple.600">
                  Classe 7 - Produits
                </Text>
                <Text fontWeight="bold" color="purple.700">
                  {chartAccounts.filter((c) => c.chart_account?.code?.startsWith('7')).length}
                </Text>
              </Box>
            </SimpleGrid>
          </Box>
        ) : (
          <Box textAlign="center" py={6} color="gray.500">
            <BookOpen size={32} style={{ margin: '0 auto 8px' }} />
            <Text>Plan comptable non initialisé</Text>
            <Text fontSize="sm" mt={1}>
              Cliquez sur "Initialiser" pour créer le plan comptable réglementaire
            </Text>
          </Box>
        )}
      </Box>

      {/* Modales */}
      <ChartAccountsManageModal
        isOpen={isChartAccountsModalOpen}
        onClose={() => setIsChartAccountsModalOpen(false)}
        residenceId={residenceId}
      />

      <DistributionKeyModal
        isOpen={isDistributionKeyModalOpen}
        onClose={() => {
          setIsDistributionKeyModalOpen(false)
          setEditingDistributionKeyId(undefined)
        }}
        residenceId={residenceId}
        distributionKeyId={editingDistributionKeyId}
      />

      <ConfirmDeleteDialog
        isOpen={!!deleteDialogKey}
        onClose={() => setDeleteDialogKey(null)}
        onConfirm={handleConfirmDeleteKey}
        entityName={deleteDialogKey?.name || ''}
        entityType="clé de répartition"
        warningMessage="Cette action est irréversible. Tous les paramètres de répartition associés à cette clé seront supprimés."
      />
    </SimpleGrid>
  )
}
